// ─── Funnel Tracking Helper ───────────────────────────────────────────────────
//
// Usage:
//   import { initFunnelSession, track } from '../../utils/funnelTracking';
//   import { VIEW_KEYS, EVENT_TYPES } from '../../utils/funnelConstants';
//
//   // Once, when the user enters /diagnosis:
//   initFunnelSession({ isLoggedInAtEntry: !!user, isNewUserFlow: !user });
//
//   // To record a step:
//   track(VIEW_KEYS.ELIGIBILITY_INFO, EVENT_TYPES.VIEWED, { userId: user?.objectId });
//
// All tracking calls are fire-and-forget. They never block or throw.
// ─────────────────────────────────────────────────────────────────────────────

import {
  FUNNEL_TRACKING_ENABLED,
  PAUSED_VIEW_KEYS,
} from './funnelConstants';
import { createRepeatGuard } from './eventDedupe';
import { captureEvent } from './posthogClient';
import { posthogEventFor } from './posthogEvents';
import { isSensitiveServiceCategory } from './posthogPolicy';

const SESSION_ID_KEY  = 'funnel_session_id';
const SEQ_KEY         = 'funnel_seq';
const LOGGED_IN_KEY   = 'funnel_logged_in_at_entry';
const NEW_USER_KEY    = 'funnel_is_new_user';

/**
 * Call once when the user first lands on /diagnosis (after user context loads).
 * Stores session metadata in sessionStorage so every subsequent track() call
 * picks up the right isLoggedInAtEntry / isNewUserFlow values automatically.
 *
 * Safe to call multiple times — only initializes once per browser session.
 */
export function initFunnelSession({ isLoggedInAtEntry, isNewUserFlow }) {
  if (typeof window === 'undefined') return;

  // sessionStorage throws (not returns null) when the browser blocks site
  // data — Safari private mode, cookies-blocked Chrome, some in-app
  // webviews. Tracking must never take the page down with it.
  try {
    // Only initialise once per session
    if (sessionStorage.getItem(SESSION_ID_KEY)) return;

    const id = `fs_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
    sessionStorage.setItem(SEQ_KEY, '0');
    sessionStorage.setItem(LOGGED_IN_KEY, String(Boolean(isLoggedInAtEntry)));
    sessionStorage.setItem(NEW_USER_KEY, String(Boolean(isNewUserFlow)));
  } catch {
    // Storage unavailable — track() falls back to a per-call session id.
  }
}

/**
 * Read the current condition off localStorage's `diseaseData`, shaped for
 * track()'s serviceId/serviceName opts.
 *
 * Only safe inside the diagnosis flow, which is always entered through
 * /diagnosis?diseaseID=X — that page rewrites `diseaseData` on every mount,
 * so it is fresh for the attempt in progress. Do NOT rely on it anywhere
 * reachable by a direct/resume link (checkout is the notable one): there it
 * can hold whatever condition was last diagnosed in this browser. Prefer a
 * diagnosisID-scoped source in those places and pass it explicitly.
 */
export function serviceFromStorage() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem('diseaseData');
    if (!raw) return {};
    const d = JSON.parse(raw);
    return { serviceId: d?.objectId ?? null, serviceName: d?.title ?? null };
  } catch {
    return {};
  }
}

/**
 * The service name for a PostHog event, or null when naming it would disclose
 * a sexual health interest. Kept separate from serviceFromStorage() so the
 * DiagnosisFlowEvent payload it feeds is unchanged.
 *
 * The category pointer in diseaseData is unexpanded, so only its objectId is
 * available — see SENSITIVE_CATEGORY_IDS.
 *
 * A caller that knows the service passes both the name and its category id,
 * and that is preferred over storage: the first sign-up event fires from a
 * mount effect that runs before the effect writing diseaseData, so at that
 * point storage is either empty or still holds the previous visit's condition.
 * Falling back to storage covers the later pages, where it is long written.
 */
function posthogServiceName(opts = {}) {
  if (opts.serviceCategoryId !== undefined) {
    return isSensitiveServiceCategory(opts.serviceCategoryId)
      ? null
      : opts.serviceName ?? null;
  }
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('diseaseData');
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (isSensitiveServiceCategory(d?.category?.objectId)) return null;
    return d?.title ?? null;
  } catch {
    return null;
  }
}

// _app.js re-keys the page when a client-side navigation completes
// (<Component key={pageKey}>), so the page mounts twice and every mount
// effect runs twice. Drop an exact repeat, or each in-site arrival would be
// recorded as two page views. See utils/eventDedupe.js: a repeat only counts
// as a double mount when nothing was clicked or typed in between, which is
// what stops a patient stepping back and forward from being thrown away.
const isRepeat = createRepeatGuard();

/**
 * Fire a funnel tracking event. Always fire-and-forget — never awaited.
 *
 * @param {string} viewKey     - One of VIEW_KEYS.*
 * @param {string} eventType   - One of EVENT_TYPES.*
 * @param {object} [opts]
 * @param {string} [opts.userId]       - Parse objectId of the user (null if not yet logged in)
 * @param {string} [opts.diagnosisId]  - Parse objectId of the Diagnosis (null until created)
 * @param {string} [opts.status]       - Optional status string
 * @param {string} [opts.note]         - Optional free-text note (no PHI)
 * @param {string} [opts.errorMessage] - Error message on failure events
 * @param {string} [opts.serviceId]    - Service/disease objectId the step is for
 * @param {string} [opts.serviceName]  - Human-readable service title
 * @param {boolean} [opts.beacon]     - Send via navigator.sendBeacon, for events
 *   fired as the page unloads (see utils/diagnosisTracking.js)
 */
export function track(viewKey, eventType, opts = {}) {
  if (typeof window === 'undefined') return;

  // `note` is part of the signature because step-level events reuse one key
  // and distinguish themselves by it (medical_section carries the section
  // name). Without it, advancing two sections inside the window would look
  // like a duplicate and the second would be dropped. A genuine double mount
  // repeats the note too, so it is still caught.
  const sig = `${viewKey}|${eventType}|${opts.note ?? ''}|${window.location.pathname}`;
  if (isRepeat(sig)) return;

  // PostHog is forwarded below the de-duplication above but above the pause
  // below. It used to sit above both, so every remount _app.js caused sent a
  // second copy to PostHog while DiagnosisFlowEvent correctly dropped it:
  // that is why the sign-up steps arrived in pairs.
  //
  // Only the handful of keys in posthogEvents are forwarded. No note,
  // errorMessage or free text goes with them, since those can carry what a
  // patient typed.
  const phEvent = posthogEventFor(viewKey);
  if (phEvent) {
    // Never opts.serviceName on its own: a name is only sent once its
    // category has been checked.
    const name = posthogServiceName(opts);
    captureEvent(phEvent, { service_name: name ?? undefined });
  }

  // Whole flow paused — see FUNNEL_TRACKING_ENABLED.
  if (!FUNNEL_TRACKING_ENABLED) return;
  // Paused tests send nothing — see PAUSED_VIEW_KEYS.
  if (PAUSED_VIEW_KEYS.has(viewKey)) return;

  // Everything below is wrapped: track() is called from inside the payment
  // success handler, so a throw here would strand a patient whose card was
  // already charged. sessionStorage throws outright when the browser blocks
  // site data, so this is a real path, not a theoretical one.
  try {
    let sessionId;
    let seq = 1;
    let isLoggedInAtEntry = false;
    let isNewUserFlow = true;

    try {
      // Ensure a session exists even if initFunnelSession was not called
      if (!sessionStorage.getItem(SESSION_ID_KEY)) {
        const id = `fs_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        sessionStorage.setItem(SESSION_ID_KEY, id);
        sessionStorage.setItem(SEQ_KEY, '0');
      }

      sessionId = sessionStorage.getItem(SESSION_ID_KEY);

      // Increment and store sequence number
      seq = parseInt(sessionStorage.getItem(SEQ_KEY) || '0', 10) + 1;
      sessionStorage.setItem(SEQ_KEY, String(seq));

      isLoggedInAtEntry = sessionStorage.getItem(LOGGED_IN_KEY) === 'true';
      isNewUserFlow = sessionStorage.getItem(NEW_USER_KEY) !== 'false'; // default true
    } catch {
      // Storage blocked. Still send the event with a one-off session id so
      // the step is counted; it just can't be stitched into a funnel.
      sessionId = `fs_nostore_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 9)}`;
    }

    const payload = {
      sessionId,
      sequence: seq,
      viewKey,
      eventType,
      route: window.location.pathname,
      isLoggedInAtEntry,
      isNewUserFlow,
      userId: opts.userId ?? null,
      diagnosisId: opts.diagnosisId ?? null,
      status: opts.status ?? null,
      note: opts.note ?? null,
      errorMessage: opts.errorMessage ?? null,
      serviceId: opts.serviceId ?? null,
      serviceName: opts.serviceName ?? null,
    };

    const body = JSON.stringify(payload);

    // Events fired as the page goes away — someone closing the tab midway
    // through a visit — need sendBeacon. A keepalive fetch is specified to
    // survive unload but is not dependable in practice; sendBeacon is built
    // for it and the browser takes ownership of delivery.
    if (opts.beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const queued = navigator.sendBeacon(
          '/api/tracking/funnel-event',
          new Blob([body], { type: 'application/json' })
        );
        if (queued) return;
        // Not queued (payload too large, or disabled) — fall through to fetch.
      } catch {
        // Fall through to fetch.
      }
    }

    // Fire and forget — tracking must never break the user flow
    fetch('/api/tracking/funnel-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // Let the request finish if the page unloads right after the event
      // (e.g. _app.js turning a navigation into a full page load).
      keepalive: true,
    }).catch(() => {
      // Intentionally silent — a tracking failure is never surfaced to the user
    });
  } catch {
    // Never surface a tracking failure to the user, under any circumstance.
  }
}
